import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Normalize a FastAPI / Axios error into a human-friendly string.
 *
 * FastAPI 422 responses return `detail` as an ARRAY of objects
 * `{ type, loc, msg, input, url }`. Rendering that array directly in JSX
 * crashes React ("Objects are not valid as a React child"). Use this helper
 * on the client side of every axios .catch() before feeding the message to
 * toast / setState / JSX.
 */
export function formatApiError(err, fallback = 'Error inesperado') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object') {
          const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
          return loc ? `${loc}: ${d.msg || 'inválido'}` : (d.msg || 'inválido');
        }
        return String(d);
      })
      .join('; ');
  }
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return err?.message || fallback;
}

// Format currency
export function formatCurrency(amount, currency = 'MXN') {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

// Format date
// CDMX / CST (fixed UTC-6, without DST — since Nov 2022). All formatters
// pin the timezone so the report reads the same regardless of the browser's
// system timezone (relevant when running in a cloud pod or when the user
// travels).
const CDMX_TIMEZONE = 'America/Mexico_City';

export function formatDate(date, options = {}) {
  if (!date) return '';
  let d;
  if (typeof date === 'string') {
    // Avoid TZ shift for YYYY-MM-DD strings: anchor to local noon so the day stays stable.
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date);
    d = isoDateOnly ? new Date(date + 'T12:00:00') : new Date(date);
  } else {
    d = date;
  }
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: CDMX_TIMEZONE,
    ...options,
  }).format(d);
}

// Format time
export function formatTime(time) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// Format datetime
export function formatDateTime(datetime) {
  if (!datetime) return '';
  const d = typeof datetime === 'string' ? new Date(datetime) : datetime;
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: CDMX_TIMEZONE,
  }).format(d);
}

// Get relative time
export function getRelativeTime(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now - d;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Ahora';
  if (minutes < 60) return `Hace ${minutes} min`;
  if (hours < 24) return `Hace ${hours}h`;
  if (days < 7) return `Hace ${days} días`;
  return formatDate(d);
}

// Get initials
export function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

// Status labels
export const appointmentStatusLabels = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  in_progress: 'En proceso',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
};

// Payment method labels
export const paymentMethodLabels = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  debit: 'TDD (Débito)',
  credit: 'TDC (Crédito)',
  amex: 'Amex',
  transfer: 'Transferencia',
  mixed: 'Mixto',
};

// Expense category labels
export const expenseCategoryLabels = {
  rent: 'Renta',
  utilities: 'Servicios',
  supplies: 'Insumos',
  payroll: 'Nómina',
  marketing: 'Marketing',
  maintenance: 'Mantenimiento',
  other: 'Otro',
};

// User role labels
export const userRoleLabels = {
  master_admin: 'Super Admin',
  admin: 'Administrador',
  barber: 'Barbero',
  stylist: 'Estilista',
  client: 'Cliente',
};

// Inventory movement type labels
export const inventoryMovementLabels = {
  entry: 'Entrada',
  sale: 'Venta',
  service_use: 'Salida-insumo',
  waste: 'Merma / Caducó',
  adjustment: 'Ajuste',
};

// Days of week
export const daysOfWeek = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

// Generate time slots
export function generateTimeSlots(start = '08:00', end = '22:00', interval = 30) {
  const slots = [];
  const [startHour, startMin] = start.split(':').map(Number);
  const [endHour, endMin] = end.split(':').map(Number);
  
  let current = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  
  while (current <= endMinutes) {
    const h = Math.floor(current / 60);
    const m = current % 60;
    slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    current += interval;
  }
  
  return slots;
}

// Calculate end time
export function calculateEndTime(startTime, durationMinutes) {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60);
  const endMinutes = totalMinutes % 60;
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
}

// Get today's date in YYYY-MM-DD format
export function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

// Get start of week
export function getStartOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

// Get end of week
export function getEndOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() + (7 - day);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

// Get start of month
export function getStartOfMonth(date = new Date()) {
  const d = new Date(date);
  d.setDate(1);
  return d.toISOString().split('T')[0];
}

// Get end of month
export function getEndOfMonth(date = new Date()) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Generate random color
export function generateColor(seed) {
  const colors = [
    'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
    'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
    'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
    'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500',
  ];
  const index = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
}


// Extract a human-readable string from any backend error shape.
// FastAPI returns validation errors as an array of { loc, msg, type } objects;
// rendering that array directly in JSX would crash React.
export function getErrorMessage(error, fallback = 'Ocurrió un error') {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : '';
        return field ? `${field}: ${d?.msg || ''}` : d?.msg || '';
      })
      .filter(Boolean)
      .join(' · ') || fallback;
  }
  if (typeof detail === 'object') return detail?.msg || JSON.stringify(detail);
  return fallback;
}

// Strip empty strings, nulls and empty arrays from an object before POST/PATCH.
// Useful so Pydantic Optional[EmailStr] doesn't get "" (which fails validation).
export function cleanPayload(obj, { keepEmptyArrays = false } = {}) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v) && !keepEmptyArrays && v.length === 0) continue;
    out[k] = v;
  }
  return out;
}
